import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  LinearProgress,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import ItemRenderer from "../items/ItemRenderer";
import ConfirmationDialog from "../common/ConfirmationDialog";

/**
 * A component for displaying checklist details
 *
 * @param {Object} props
 * @param {Object} props.checklist - Checklist data
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 * @param {Function} props.onDuplicate - Callback when duplicate button is clicked
 * @param {Function} props.onUpdateItem - Callback when an item is updated
 */
const ChecklistDetail = ({
  checklist,
  onEdit,
  onDelete,
  onDuplicate,
  onUpdateItem,
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!checklist) {
    return null;
  }

  const { id, title, description, items = [] } = checklist;

  const completedItems = items.filter((item) => item.completed);
  const progress =
    items.length > 0
      ? Math.round((completedItems.length / items.length) * 100)
      : 0;

  const getStatusChip = () => {
    let status;
    if (progress === 100) {
      status = "completed";
    } else if (progress === 0) {
      status = "not_started";
    } else {
      status = "in_progress";
    }

    switch (status) {
      case "completed":
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Completed"
            color="success"
            variant="outlined"
          />
        );
      case "in_progress":
        return (
          <Chip
            icon={<ScheduleIcon />}
            label="In Progress"
            color="primary"
            variant="outlined"
          />
        );
      case "not_started":
        return (
          <Chip
            icon={<ErrorIcon />}
            label="Not Started"
            color="warning"
            variant="outlined"
          />
        );
      default:
        return null;
    }
  };

  const handleItemUpdate = (itemId, updates) => {
    onUpdateItem(id, itemId, updates);
  };

  const handleDeleteClick = () => {
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    onDelete(id);
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexDirection: isMobile ? "column" : "row",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              {title}
            </Typography>

            {description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {description}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: isMobile ? 2 : 0,
              }}
            >
              {getStatusChip()}
              <Typography variant="body2" color="text.secondary">
                {completedItems.length}/{items.length} items completed
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: isMobile ? "flex-start" : "flex-end",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {/* <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit(id)}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileCopyIcon />}
              onClick={() => onDuplicate(id)}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Duplicate
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Delete
            </Button> */}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          {/* <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              mb: 1,
            }}
          />
          <Typography variant="body2" color="text.secondary" align="right">
            {progress}% Complete
          </Typography> */}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Checklist Items
        </Typography>

        {items.length > 0 ? (
          <Box>
            {items.map((item) => (
              <ItemRenderer
                key={item.id}
                item={item}
                onUpdate={handleItemUpdate}
                onDelete={() => {}}
                draggable={false}
                isTemplateView={false}
              />
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            No items in this checklist.
          </Typography>
        )}
      </Paper>

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Checklist"
        message="Are you sure you want to delete this checklist? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />
    </>
  );
};

export default ChecklistDetail;
